import {resolveLocationAccountId as resolveLocationThryvId} from './locationIdentity'

// Builds the PurchaseLinkSlice seed (accountInfo + locations) for the new
// proposal-tool-style "Create Purchase Link" flow, replacing
// buildV5UpgradeOrder as the v5-upgrade destination from
// handleOrder/handleSaveAndProceed. The caller dispatches setAccountInfo/
// initLocations with this return value, then setStep(2) + navigate to
// /accounts/account-setup to land on Account Setup's Catalog Selection step
// (see AccountSetup/components/CatalogSelectionStep.js) — no dedicated route.
// Unlike buildV5UpgradeOrder (a single order for the one thryvId that was
// clicked), this carries the *whole* account hierarchy (parent + every child
// location) so the location dropdown can switch between them without a
// second round-trip back to the account page.

// Builds the accountInfo shape (businessName/email/firstName/lastName/phone/
// mobile/country/xpCode/salesRepOrPartner/billTo/address/city/state/postCode)
// for a single account-like record — `source` is either the `parentAccount`
// or one `childAccount`, both normalized identically by the billing-service
// client (same field names: company/email/firstName/lastName/phone/address.{
// street1,city,region,postalCode,country}). `recurlyAccountInfo` (the
// accounts/5/subscriptions Recurly fetch) and `editValues` (the rep's Edit
// modal input) are only ever fetched/typed for ONE record per call — the
// entity the rep actually clicked/edited — so callers only pass them for
// that record, leaving every other one (e.g. sibling locations shown in the
// dropdown but not the one being ordered) with just its own raw fields.
function buildAccountInfo(
  source,
  {recurlyAccountInfo = {}, editValues = {}} = {},
) {
  const companyName =
    source?.company || source?.companyName || recurlyAccountInfo?.company || ''
  return {
    // Billing Service exposes the parent EAID in `code`, but Recurly
    // identifies the billing account by id. Sending the EAID to
    // cc/order/send-link persists an unusable order.thryv_id and makes
    // save-billing fail with a Recurly account-not-found error.
    thryvId: resolveLocationThryvId(source),
    businessName: editValues?.businessName || companyName,
    email:
      editValues?.email ||
      recurlyAccountInfo?.email ||
      source?.email ||
      source?.accountEmail ||
      null,
    firstName:
      editValues?.firstName ||
      recurlyAccountInfo?.firstName ||
      source?.firstName ||
      null,
    lastName:
      editValues?.lastName ||
      recurlyAccountInfo?.lastName ||
      source?.lastName ||
      null,
    phone:
      editValues?.telephone ||
      recurlyAccountInfo?.phone ||
      source?.phone ||
      source?.address?.phone ||
      null,
    mobile:
      editValues?.mobile ||
      recurlyAccountInfo?.mobile ||
      source?.mobile ||
      null,
    country: source?.country || source?.address?.country || 'US',
    xpCode:
      editValues?.xpCode ||
      recurlyAccountInfo?.xpCode ||
      source?.xpCode ||
      null,
    salesRepOrPartner:
      editValues?.salesRepOrPartner ||
      recurlyAccountInfo?.salesRepOrPartner ||
      source?.salesRepOrPartner ||
      null,
    billTo: source?.billTo || null,
    // Forwarded to order_header via buildCreateOrderPayload/buildSendLinkPayload
    // so the payment page's "Same as my business address" checkbox has an
    // actual address to copy from — editValues (Edit modal) takes priority
    // over the Recurly-sourced source.address, same fallback order as the
    // rest of this object.
    address: editValues?.address || source?.address?.street1 || null,
    city: editValues?.city || source?.address?.city || null,
    state:
      editValues?.state?.value ||
      editValues?.state ||
      source?.address?.region ||
      source?.address?.state ||
      null,
    postCode: editValues?.postCode || source?.address?.postalCode || null,
  }
}

export function buildPurchaseLinkState({
  // The true parent account this purchase link was opened for. Every caller
  // resolves this itself (it's the account/parentAccount/parentAccountForOrder
  // it already has in scope, falling back to the entity being edited only
  // when that entity already IS the parent, e.g. a pre-5 account edit) and
  // passes the result here — this function does no fallback resolution of
  // its own.
  parentAccount,
  accountSubscriptions,
  selectedLocation,
  editValues = {},
  // A combined parentPay set, or a single newly-created location — always
  // wins over parentAccount's own `.locations` when supplied, instead of
  // callers spreading it onto a fabricated account object.
  locationsOverride,
  // The logged-in rep creating this purchase link — selectUserData's
  // user?.email / selectEffectiveRole's effectiveRole?.[0], same sourcing
  // ProposalCreationComponent uses for proposalInfo.salesRepId/userRole.
  // Not an attribute of the account/location being ordered for (it's the
  // same rep regardless of which location's accountInfo ends up read as
  // `identity`), so it's stamped directly onto every accountInfo below
  // instead of being threaded into buildAccountInfo, which only knows about
  // the account/location record itself.
  salesRepId = null,
  userRole = null,
}) {
  const recurlyAccountInfo = accountSubscriptions?.accountInfo ?? {}
  const selectedLocationThryvId =
    selectedLocation && resolveLocationThryvId(selectedLocation)

  // handleOrder passes the full parent-with-children object
  // (parentAccount.locations = every sibling location), so the dropdown
  // can switch between them. handleSaveAndProceed (triggered from the
  // per-location Edit modal) only ever has the single location/account
  // being edited, with no `.locations` of its own — fall back to treating
  // it as the sole location so the flow still works there, just without
  // sibling switching.
  const locations = (
    locationsOverride ?? (selectedLocation ? [selectedLocation] : [])
  ).map(loc => {
    const locThryvId = resolveLocationThryvId(loc)
    const isSelected = locThryvId === selectedLocationThryvId
    return {
      locationId: locThryvId,
      locationName:
        loc?.name || loc?.company || loc?.companyName || locThryvId || '',
      thryvId: locThryvId,
      country:
        loc?.country ||
        loc?.address?.country ||
        parentAccount?.country ||
        parentAccount?.address?.country ||
        'US',
      billTo: loc?.billTo === 'self' ? 'self' : 'parent',
      email: loc?.email || loc?.accountEmail || null,
      // This location's own identity/address, distinct from the parent
      // `accountInfo` below — buildSendLinkPayload reads THIS instead of
      // the parent's for a single-location ('location' orderMode) order,
      // so its send-link payload (and the Recurly account it targets)
      // matches the location actually being ordered, not the parent
      // account it happens to hang off of. Only the selected location gets
      // the freshly-fetched Recurly accountInfo/editValues layered on —
      // sibling locations in the dropdown were never fetched/edited this
      // round.
      accountInfo: buildAccountInfo(loc, {
        recurlyAccountInfo: isSelected ? recurlyAccountInfo : {},
        editValues: isSelected ? editValues : {},
      }),
    }
  })

  return {
    // The parent account this purchase link was opened for — used as-is,
    // whether this is a single-location or combined parentPay order.
    // recurlyAccountInfo/editValues always belong to the clicked/edited
    // child location (see the param doc above), never to the parent, so
    // they're withheld here and this always falls back to the parent's own
    // raw fields instead. See each location's own `accountInfo` above for
    // the single-location case.
    accountInfo: buildAccountInfo(parentAccount, {}),
    // Kept at this same top level, not nested inside accountInfo/each
    // location's own accountInfo — see the param doc above for why.
    salesRepId,
    userRole,
    // The rep's sales/partner attribution choices from the "Create Purchase
    // Link" modal (AccountEditModal's hideLocationDetails view) — editValues
    // carries these when the caller went through that modal (Save/Continue),
    // and is {} otherwise (e.g. the direct-upgrade shortcut that skips it),
    // in which case this comes back as all-null defaults.
    salesAttributionFields: {
      salesRepOrPartner: editValues?.salesRepOrPartner ?? null,
      xpCode: editValues?.xpCode ?? null,
      overrideSST: editValues?.overrideSST ?? null,
      salesRepInfo: editValues?.salesRepInfo ?? null,
      overrideRepInfo: editValues?.overrideRepInfo ?? null,
    },
    locations,
    selectedLocationId:
      selectedLocationThryvId || locations[0]?.locationId || null,
  }
}
